/**
 * Intake BE(spnp) `/v1/candidates` 경계.
 *
 * **수락은 되풀이해도 같은 데이터셋이다** — 이번에 만들었으면 `201`, 이미 있으면 `200` 으로
 * 같은 행을 준다. 폐기는 되돌리지 않으므로 화면이 확인을 한 번 받고 부른다.
 *
 * 목록은 기본으로 아직 보지 않은 후보(`discovered`)만 받는다. 수락된 것은 데이터셋 목록에 있고
 * 폐기된 것은 다시 제안하지 않기로 한 것이라 둘 다 기본 화면의 대상이 아니다.
 *
 * Owns:
 * - 요청 · 쿼리 구성, 응답 모양 확인과 `Candidate` 변환, 201/200 구분.
 *
 * Does not own:
 * - Bearer 첨부 · 세션 무효화 — `apiClient`. Dataset 변환 — `api/datasetApi.ts`.
 */
import {
  apiClient,
  IntakeContractError,
  readBoolean,
  readBoundedString,
  readInstantMs,
  readNullableString,
  readRecord,
  readString,
} from "@/shared/http";
import { CANDIDATE_STATES, type Candidate, type CandidateState } from "../domain/candidate";
import { DATASET_FORMATS } from "../domain/dataset";
import { parseDataset } from "./datasetApi";
import type { Dataset } from "../domain/dataset";

const RESOURCE = "candidate";

export type AcceptCandidateOutcome = {
  dataset: Dataset;
  /** 이번 호출이 데이터셋을 만들었는지(`201`). `false` 면 이미 있던 것이다(`200`). */
  created: boolean;
};

export async function listCandidates(
  states: readonly CandidateState[] = ["discovered"],
): Promise<readonly Candidate[]> {
  const response = await apiClient.get<unknown>("/v1/candidates", {
    // 같은 이름을 여러 번 싣는 쿼리라 배열을 그대로 넘긴다.
    params: { state: states },
  });
  const body = readRecord(response.data, RESOURCE, "body");
  if (!Array.isArray(body.candidates)) {
    throw new IntakeContractError(RESOURCE, "candidates");
  }

  return body.candidates.map(parseCandidate);
}

export async function acceptCandidate(
  candidateUuid: string,
  displayName?: string,
): Promise<AcceptCandidateOutcome> {
  // 본문 없이 보내면 422 다 — 이름을 바꾸지 않을 때도 객체를 싣는다.
  const response = await apiClient.post<unknown>(
    `/v1/candidates/${candidateUuid}/accept`,
    displayName === undefined ? {} : { display_name: displayName },
  );

  return { dataset: parseDataset(response.data), created: response.status === 201 };
}

export async function dismissCandidate(candidateUuid: string, reason?: string): Promise<Candidate> {
  const response = await apiClient.post<unknown>(
    `/v1/candidates/${candidateUuid}/dismiss`,
    reason === undefined ? {} : { reason },
  );
  return parseCandidate(response.data);
}

function parseCandidate(value: unknown): Candidate {
  const row = readRecord(value, RESOURCE, "row");

  return {
    uuid: readString(row.uuid, RESOURCE, "uuid"),
    rootCode: readString(row.root_code, RESOURCE, "root_code"),
    sourceRef: readString(row.source_ref, RESOURCE, "source_ref"),
    format: readBoundedString(row.format, DATASET_FORMATS, RESOURCE, "format"),
    formatVersion: readNullableString(row.format_version, RESOURCE, "format_version"),
    formatDetail: readNullableString(row.format_detail, RESOURCE, "format_detail"),
    canProbe: readBoolean(row.can_probe, RESOURCE, "can_probe"),
    state: readBoundedString(row.state, CANDIDATE_STATES, RESOURCE, "state"),
    firstSeenAtMs: readInstantMs(row.first_seen_at, RESOURCE, "first_seen_at"),
    lastSeenAtMs: readInstantMs(row.last_seen_at, RESOURCE, "last_seen_at"),
    dismissedReason: readNullableString(row.dismissed_reason, RESOURCE, "dismissed_reason"),
  };
}
